// client/src/components/pages/LexiStar/NotificationRules/ruleLogic.ts (converted from .js, ST-00205)
//
// Pure logic for the notification rule builder (LS-09 §5.16; FR-NTF-001…009,
// FR-NTF-012; m3-contracts §3.2 notification_rules): the event catalogue the
// "When" picker offers, recipient kinds, channels, condition operators, the
// natural-language summary of a rule, client-side validation (mirrors the
// API's Joi rules so mistakes show inline), and sample template variables in
// the evaluator's shape for the live preview.
//
// ST-00205: the catalogue offers the trips API's dispatch requests (TRIP.DISPATCH_REQUESTED,
// RUN.TRIP_APPEND_REQUESTED), which migration 063 adds to the driver.trip_assignment and
// driver.next_load rules so the driver is messaged at dispatch. Converted to TypeScript (the
// CLAUDE.md rule); the exports and their behaviour are unchanged. API shapes:
// types/lexistar-notification-rules.
//
// ST-00261 (migration 068): the seeded driver.trip_assignment carries the condition
// payload.command is not append_to_run (a next load sends only the next-load prompt), so
// payload.command is one of the suggested condition paths.
//
// ST-00280: the catalogue and the words for its types come from utils/eventTypeLabels (one label
// catalogue for every page); the picker, the rule sentence and the preview's sample variables
// show a type in words with its code beside it.

import type {
  NotificationRulePayload,
  NotificationRuleRow,
  RuleCondition,
  RuleEscalationTier,
  RuleEventFilter,
  RuleRecipient,
  RuleThrottle,
} from '../../../../types/lexistar-notification-rules';
import {
  CATALOGUE_EVENT_TYPES,
  eventCategoryNoun,
  eventCategoryOf,
  eventTypeLabel,
  eventTypePhrase,
} from '../../../../utils/eventTypeLabels';

export interface Option<V extends string = string> {
  value: V;
  label: string;
}

export const SEVERITIES: Option[] = [
  { value: 'I', label: 'Info' },
  { value: 'L', label: 'Low' },
  { value: 'M', label: 'Medium' },
  { value: 'H', label: 'High' },
  { value: 'C', label: 'Critical' },
];
export const severityLabel = (s: string): string => SEVERITIES.find((x) => x.value === s)?.label || s;

/**
 * The event types the "When" picker offers: @lexistar/domain's EVENT_CATALOGUE (utils/eventTypeLabels
 * CATALOGUE_EVENT_TYPES — M1–M5, the trips API's requests of ST-00205 included), grouped by
 * category in the catalogue's category order.
 */
export const EVENT_TYPES: string[] = (() => {
  const byCategory = new Map<string, string[]>();
  for (const t of CATALOGUE_EVENT_TYPES) {
    const c = eventCategoryOf(t);
    byCategory.set(c, [...(byCategory.get(c) || []), t]);
  }
  return [...byCategory.values()].flat();
})();

export const CATEGORIES: string[] = [...new Set(EVENT_TYPES.map((t) => t.split('.')[0] as string))];

/**
 * Picker options: a `CATEGORY.*` wildcard per category, then every catalogue type — in words,
 * the code after them (ST-00280: "Arrived at source (TRIP.ARRIVED_SOURCE)").
 */
export const EVENT_TYPE_OPTIONS: Option[] = [
  ...CATEGORIES.map((c) => ({ value: `${c}.*`, label: `Every ${eventCategoryNoun(c)} event (${c}.*)` })),
  ...EVENT_TYPES.map((t) => ({ value: t, label: `${eventTypeLabel(t)} (${t})` })),
];

export const RULE_CHANNELS: Option[] = [
  { value: 'in_app', label: 'In-app' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'sms', label: 'SMS' },
  { value: 'fake', label: 'Test (fake)' },
];
export const channelName = (c: string): string => RULE_CHANNELS.find((x) => x.value === c)?.label || c;

export interface RecipientKind extends Option {
  needsRef: boolean;
  hint?: string;
}

export const RECIPIENT_KINDS: RecipientKind[] = [
  {
    value: 'role',
    label: 'Everyone with a permission',
    needsRef: true,
    hint: 'lexistar.trips.view',
  },
  { value: 'user', label: 'A user', needsRef: true },
  { value: 'group', label: 'A recipient group', needsRef: true },
  { value: 'driver', label: "The truck's driver", needsRef: false },
  { value: 'dispatcher', label: "The trip's dispatcher", needsRef: false },
  { value: 'contact', label: 'An external contact', needsRef: true, hint: '+27821234567' },
  { value: 'telegram_group', label: 'A Telegram group', needsRef: true, hint: '-1001234567890' },
];
export const GROUP_MEMBER_KINDS: string[] = ['user', 'role', 'contact', 'telegram_group'];
export const recipientKindLabel = (k: string): string =>
  RECIPIENT_KINDS.find((x) => x.value === k)?.label || k;

export const ROLE_SUGGESTIONS: string[] = [
  'lexistar.trips.view',
  'lexistar.trips.manage',
  'lexistar.trips.dispatch',
  'lexistar.deviations.approve',
  'lexistar.review.triage',
  'lexistar.notifications.manage',
];

export const CONDITION_OPS: Option[] = [
  { value: 'eq', label: 'is' },
  { value: 'ne', label: 'is not' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'in', label: 'is one of' },
  { value: 'not_in', label: 'is none of' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'exists', label: 'is present' },
  { value: 'not_exists', label: 'is missing' },
];
export const opLabel = (op: string): string => CONDITION_OPS.find((o) => o.value === op)?.label || op;
const NO_VALUE_OPS = ['exists', 'not_exists'];
const LIST_OPS = ['in', 'not_in'];

export const CONDITION_PATHS: string[] = [
  // ST-00261: the trip command of TRIP.DISPATCH_REQUESTED / TRIP.DISPATCHED (dispatch | append_to_run)
  'payload.command',
  'payload.off_route_km',
  'payload.max_offset_m',
  'payload.excess_l',
  'payload.variance_kg',
  'payload.dwell_s',
  'payload.speed_kmh',
  'confidence',
  'asset_key',
  'route_id',
  'geofence_id',
];

/** The rule builder's editable model: every optional piece of a rule present. */
export interface RuleModel {
  id: string | null;
  name: string;
  active: boolean;
  event_filter: Required<Pick<RuleEventFilter, 'types' | 'categories' | 'severities'>> & {
    conditions: RuleCondition[];
  };
  recipients: RuleRecipient[];
  channels: string[];
  template_key: string | null | undefined;
  throttle: RuleThrottle;
  escalation: RuleEscalationTier[];
  version?: number;
  [column: string]: unknown;
}

export function newRule(): RuleModel {
  return {
    id: null,
    name: '',
    active: true,
    event_filter: { types: [], categories: [], severities: [], conditions: [] },
    recipients: [{ kind: 'role', ref: 'lexistar.trips.view' }],
    channels: ['in_app'],
    template_key: 'CONTROLLER_EVENT',
    throttle: {
      per_rule_s: 0,
      dedupe_s: 600,
      quiet_hours: null,
      digest: null,
      severity_floor: null,
    },
    escalation: [],
  };
}

/** API row → editable model (every optional piece present). */
export function fromRule(row: NotificationRuleRow): RuleModel {
  const base = newRule();
  return {
    ...base,
    ...row,
    event_filter: { ...base.event_filter, ...(row.event_filter || {}) } as RuleModel['event_filter'],
    recipients: Array.isArray(row.recipients) ? row.recipients : [],
    channels: Array.isArray(row.channels) ? row.channels : [],
    throttle: { ...base.throttle, ...(row.throttle || {}) },
    escalation: Array.isArray(row.escalation) ? row.escalation : [],
  };
}

const cleanRecipient = (r: RuleRecipient): RuleRecipient => {
  const out: RuleRecipient = { kind: r.kind };
  if (r.kind === 'user') out.ref = Number(r.ref);
  else if (r.kind === 'dispatcher') {
    /* no ref */
  } else if (r.kind === 'driver') {
    if (r.ref) out.ref = r.ref;
  } else out.ref = String(r.ref ?? '').trim();
  if (r.channel) out.channel = r.channel;
  if (r.name) out.name = r.name;
  return out;
};

const parseValue = (op: string, v: unknown): unknown => {
  if (LIST_OPS.includes(op)) {
    const list: unknown[] = Array.isArray(v)
      ? v
      : String(v ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
    return list.map((x) => (x !== '' && Number.isFinite(Number(x)) ? Number(x) : x));
  }
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  const s = String(v ?? '').trim();
  if (s === 'true' || s === 'false') return s === 'true';
  return s !== '' && Number.isFinite(Number(s)) ? Number(s) : s;
};

/** Editable model → API body (create or patch). */
export function toPayload(rule: RuleModel): NotificationRulePayload {
  const t: RuleThrottle = rule.throttle || {};
  const body: NotificationRulePayload = {
    name: rule.name.trim(),
    active: !!rule.active,
    event_filter: {
      types: rule.event_filter.types || [],
      categories: rule.event_filter.categories || [],
      severities: rule.event_filter.severities || [],
      conditions: (rule.event_filter.conditions || []).map((c) =>
        NO_VALUE_OPS.includes(c.op)
          ? { path: c.path, op: c.op }
          : { path: c.path, op: c.op, value: parseValue(c.op, c.value) }
      ),
    },
    recipients: rule.recipients.map(cleanRecipient),
    channels: rule.channels,
    template_key: rule.template_key,
    throttle: {
      per_rule_s: Number(t.per_rule_s) || 0,
      dedupe_s: Number(t.dedupe_s) || 0,
      quiet_hours: t.quiet_hours ? { tz: 'Africa/Johannesburg', ...t.quiet_hours } : null,
      digest: t.digest ? { every_s: Number(t.digest.every_s) || 3600 } : null,
      severity_floor: t.severity_floor || null,
    },
    escalation: (rule.escalation || []).map((tier) => ({
      after_s: Number(tier.after_s),
      recipients: (tier.recipients || []).map(cleanRecipient),
      channels: tier.channels || [],
      ...(tier.template_key ? { template_key: tier.template_key } : {}),
    })),
  };
  if (rule.version) body.version = rule.version;
  return body;
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function recipientErrors(
  list: RuleRecipient[],
  where: string,
  kinds: string[] = RECIPIENT_KINDS.map((k) => k.value)
): string[] {
  const errors: string[] = [];
  list.forEach((r, i) => {
    const n = `${where} ${i + 1}`;
    if (!kinds.includes(r.kind)) errors.push(`${n}: choose who`);
    else if (r.kind === 'user' && !(Number.isInteger(Number(r.ref)) && Number(r.ref) > 0))
      errors.push(`${n}: choose a user`);
    else if (
      ['role', 'group', 'contact', 'telegram_group'].includes(r.kind) &&
      !String(r.ref ?? '').trim()
    )
      errors.push(`${n}: ${recipientKindLabel(r.kind).toLowerCase()} needs a value`);
    else if (r.kind === 'telegram_group' && !/^-?\d{1,19}$/.test(String(r.ref).trim()))
      errors.push(`${n}: a Telegram group id is a number`);
  });
  return errors;
}

/** Inline validation — the same limits the API enforces. */
export function validateRule(rule: RuleModel): string[] {
  const errors: string[] = [];
  if (!rule.name.trim()) errors.push('Give the rule a name');
  const f = rule.event_filter;
  if (!f.types.length && !f.categories.length && !f.severities.length) {
    errors.push(
      'Choose at least one event type, category or severity (a rule never matches everything)'
    );
  }
  (f.conditions || []).forEach((c, i) => {
    if (!/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z0-9_]+)*$/.test(c.path || ''))
      errors.push(`Condition ${i + 1}: the field is not a dot path`);
    if (!NO_VALUE_OPS.includes(c.op) && String(c.value ?? '').trim() === '')
      errors.push(`Condition ${i + 1}: give a value`);
  });
  if (!rule.recipients.length) errors.push('Add at least one recipient');
  errors.push(...recipientErrors(rule.recipients, 'Recipient'));
  if (!rule.channels.length) errors.push('Choose at least one channel');
  if (!rule.template_key) errors.push('Choose a template');
  const t: RuleThrottle = rule.throttle || {};
  if (t.quiet_hours && (!HHMM.test(t.quiet_hours.from || '') || !HHMM.test(t.quiet_hours.to || '')))
    errors.push('Quiet hours need a start and an end (HH:MM)');
  if (t.digest && !(Number(t.digest.every_s) >= 60))
    errors.push('A digest runs at most once a minute');
  let last = 0;
  (rule.escalation || []).forEach((tier, i) => {
    const n = `Escalation ${i + 1}`;
    if (!(Number(tier.after_s) >= 30)) errors.push(`${n}: wait at least 30 seconds`);
    else if (Number(tier.after_s) <= last) errors.push(`${n}: must come after the previous tier`);
    last = Number(tier.after_s) || last;
    if (!(tier.recipients || []).length) errors.push(`${n}: add a recipient`);
    errors.push(...recipientErrors(tier.recipients || [], `${n}, recipient`));
    if (!(tier.channels || []).length) errors.push(`${n}: choose a channel`);
  });
  return errors;
}

/** ST-00282: the server's `locked` as lockErrors reads it. */
export interface RuleLockLimits {
  fields?: string[];
  max_throttle_s?: number;
}

const NOGO_TYPE = 'GEOFENCE.NOGO_ENTERED';

/**
 * ST-00282: what would stop a locked rule (the seeded no-go rule: a no-go zone always alerts) from
 * alerting on every no-go entry — the server refuses the same (422 NOGO_RULE_LOCKED), this says
 * it before saving. A wider "When" is fine; a rule without a lock has none of these limits.
 */
export function lockErrors(rule: RuleModel, lock: RuleLockLimits | null | undefined): string[] {
  if (!lock) return [];
  const errors: string[] = [];
  const why = 'No-go zones always alert';
  const f = rule.event_filter;
  const types = f.types || [];
  const listens =
    types.length === 0 ||
    types.some((p) => p === '*' || p === NOGO_TYPE || (p.endsWith('*') && NOGO_TYPE.startsWith(p.slice(0, -1))));
  if (!listens) errors.push(`${why}: keep “No-go zone entered” among the event types`);
  if (f.categories.length && !f.categories.includes('GEOFENCE'))
    errors.push(`${why}: keep the Geofence category`);
  if (f.severities.length && !f.severities.includes('C'))
    errors.push(`${why}: keep Critical among the severities`);
  if ((f.conditions || []).length) errors.push(`${why}: this rule takes no conditions`);
  if (['DRIVER_ACK_REMINDER', 'DRIVER_ACK_FINAL_REMINDER'].includes(String(rule.template_key)))
    errors.push(`${why}: choose a controller template, not a driver acknowledgement reminder`);
  const t: RuleThrottle = rule.throttle || {};
  if (t.quiet_hours) errors.push(`${why}: this rule has no quiet hours`);
  if (t.digest) errors.push(`${why}: this rule sends no digest`);
  // A per-rule limit is not applied to this rule (the server stores 0): nothing to check.
  const max = lock.max_throttle_s ?? 900;
  if (t.dedupe_s != null && t.dedupe_s !== '' && !Number.isFinite(Number(t.dedupe_s)))
    errors.push(`${why}: the repeat window must be a number of minutes`);
  else if (Number(t.dedupe_s) > max)
    errors.push(`${why}: ignore repeats for at most ${max / 60} minutes`);
  return errors;
}

export function fmtSeconds(s: unknown): string {
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return 'off';
  if (n < 60) return `${n} s`;
  if (n < 3600) return `${Math.round(n / 60)} min`;
  if (n < 86400) return `${+(n / 3600).toFixed(1)} h`;
  return `${+(n / 86400).toFixed(1)} d`;
}

/** A recipient group as describeRule names it. */
interface GroupRef {
  name?: string | null;
}

const recipientPhrase = (
  r: RuleRecipient,
  groupsById: Map<unknown, GroupRef> = new Map()
): string => {
  switch (r.kind) {
    case 'role':
      return `everyone with ${r.ref}`;
    case 'user':
      return r.name || `user ${r.ref}`;
    case 'group':
      return groupsById.get(r.ref)?.name || 'a group';
    case 'driver':
      return 'the driver';
    case 'dispatcher':
      return 'the dispatcher';
    case 'contact':
      return r.name || String(r.ref);
    case 'telegram_group':
      return r.name || `Telegram group ${r.ref}`;
    default:
      return r.kind;
  }
};

/** A rule as describeRule reads it (the model, or an API row). */
type DescribableRule = Pick<RuleModel, 'template_key'> & {
  event_filter?: RuleEventFilter | null;
  recipients?: RuleRecipient[] | null;
  channels?: string[] | null;
  throttle?: RuleThrottle | null;
  escalation?: RuleEscalationTier[] | null;
};

/**
 * "When left the approved route (ROUTE.DEVIATION_OPENED) (High, Critical), notify the driver via
 * WhatsApp → SMS …" — the type in words with its code after it (ST-00280), a category as "any
 * trip event".
 */
export function describeRule(
  rule: DescribableRule,
  { groupsById }: { groupsById?: Map<unknown, GroupRef> } = {}
): string {
  const f: RuleEventFilter = rule.event_filter || {};
  const what =
    [
      ...(f.types || []).map((t) => (t.endsWith('*') ? t : `${eventTypePhrase(t)} (${t})`)),
      ...(f.categories || []).map((c) => `any ${eventCategoryNoun(c)} event`),
    ].join(', ') || 'any event';
  const sev = (f.severities || []).length ? ` (${(f.severities || []).map(severityLabel).join(', ')})` : '';
  const conditions = f.conditions || [];
  const cond = conditions.length
    ? ` and ${conditions.map((c) => `${c.path} ${opLabel(c.op)}${NO_VALUE_OPS.includes(c.op) ? '' : ` ${Array.isArray(c.value) ? c.value.join(', ') : String(c.value)}`}`).join(' and ')}`
    : '';
  const who =
    (rule.recipients || []).map((r) => recipientPhrase(r, groupsById)).join(', ') || 'nobody';
  const via = (rule.channels || []).map(channelName).join(' → ') || 'no channel';
  const t: RuleThrottle = rule.throttle || {};
  const limits = [
    Number(t.dedupe_s) > 0 ? `dedupe ${fmtSeconds(t.dedupe_s)}` : null,
    Number(t.per_rule_s) > 0 ? `at most once per ${fmtSeconds(t.per_rule_s)}` : null,
    t.quiet_hours ? `quiet ${t.quiet_hours.from}–${t.quiet_hours.to}` : null,
    t.digest ? `digest every ${fmtSeconds(t.digest.every_s)}` : null,
    t.severity_floor && typeof t.severity_floor === 'string'
      ? `${severityLabel(t.severity_floor)} and up`
      : null,
  ].filter(Boolean);
  const esc = (rule.escalation || [])
    .map(
      (tier) =>
        `after ${fmtSeconds(tier.after_s)} escalate to ${(tier.recipients || []).map((r) => recipientPhrase(r, groupsById)).join(', ')}`
    )
    .join('; ');
  return (
    `When ${what}${sev}${cond}, notify ${who} via ${via} using ${rule.template_key || '—'}` +
    `${limits.length ? `; ${limits.join(', ')}` : ''}${esc ? `; ${esc}` : ''}.`
  );
}

// Fixed per page load, so the preview's variables are stable between renders.
const SAMPLE_T = Math.floor(Date.now() / 60000) * 60000;
const sampleIso = (offsetMs = 0): string => new Date(SAMPLE_T + offsetMs).toISOString();

/**
 * Sample variables in the evaluator's shape (notifications/evaluator.ts varsFor) for previews.
 * ST-00280: as in the evaluator, {{event.type}}, {{event.label}} and {{type}} are the type's words
 * and {{event.code}} is the code.
 */
export function sampleVars(eventType = 'ROUTE.DEVIATION_OPENED', language = 'en'): Record<string, unknown> {
  const [category] = eventType.split('.');
  const label = eventTypeLabel(eventType);
  const trip = {
    id: '00000000-0000-4000-8000-000000000001',
    reference: 'TR-260924-0142',
    stage: 'IN_TRANSIT_LOADED',
    from: 'Mine A',
    to: 'Siding B',
    planned_km: 212,
    planned_start: sampleIso(),
    budget_l: 89,
    material: 'RB3 coal',
    registration: 'JX12KLGP',
    fleet_no: 'T-01',
    eta: sampleIso(2 * 3600000),
  };
  return {
    event: { type: label, code: eventType, label, category, severity: 'H', time: sampleIso() },
    type: label,
    category,
    severity: 'H',
    asset: 'JX12KLGP',
    registration: 'JX12KLGP',
    place: 'N4 near eMalahleni',
    time: sampleIso(),
    link: `${typeof window !== 'undefined' ? window.location.origin : ''}/lexistar/trips/${trip.id}`,
    link_path: `/lexistar/trips/${trip.id}`,
    trip,
    from: trip.from,
    to: trip.to,
    planned_km: trip.planned_km,
    planned_start: trip.planned_start,
    budget_l: trip.budget_l,
    deviation: { state: 'OPEN', off_route_km: 4.2, max_offset_m: 850, opened_at: sampleIso() },
    payload: {},
    driver: { name: 'T. Nkosi', tag: 'EMP-104' },
    recipient: { kind: 'driver', name: 'T. Nkosi' },
    rule: { name: 'Preview' },
    tenant: { name: 'Your organisation' },
    language,
    escalation: { tier: 1, after_s: 600, after_min: 10 },
  };
}
